import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Mail, Phone, MapPin, MessageCircle, HelpCircle, Send, ArrowRight } from "lucide-react";
import { Footer } from "@/components/Footer";

type InquiryType = "quote" | "support" | "general";

export default function Contact() {
  const [inquiryType, setInquiryType] = useState<InquiryType>("quote");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const getEmailAddress = () => {
    if (inquiryType === "quote") {
      return "hello@getshuttlebee.com";
    } else if (inquiryType === "support") {
      return "support@getshuttlebee.com";
    }
    return "hello@getshuttlebee.com";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const webhookData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      inquiryType: inquiryType,
      targetEmail: getEmailAddress(),
      subject: inquiryType === "quote" ? "Quote Request" : inquiryType === "support" ? "Route Support Request" : "General Inquiry",
      timestamp: new Date().toISOString(),
    };

    try {
      await fetch("https://hooks.zapier.com/hooks/catch/22972432/uwg1rad/", {
        method: "POST",
        body: JSON.stringify(webhookData),
      });

      alert("Message sent successfully! We'll get back to you soon.");

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending to Zapier:", error);
      alert("Failed to send message. Please try again or contact us directly at " + getEmailAddress());
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 pt-20 pb-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" style={{ animationDelay: '1s' }} />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <div className="animate-fade-in-up">
              <h1 className="font-display text-5xl font-bold tracking-tight text-white sm:text-6xl mb-6">
                Get in Touch
              </h1>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
                Have questions about our routes or need support? We're here to help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>

              {/* Inquiry Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What can we help you with?
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setInquiryType("quote")}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      inquiryType === "quote"
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <MessageCircle className={`w-6 h-6 mx-auto mb-2 ${
                      inquiryType === "quote" ? "text-blue-600" : "text-gray-400"
                    }`} />
                    <span className={`text-sm font-medium ${
                      inquiryType === "quote" ? "text-blue-600" : "text-gray-600"
                    }`}>
                      Quote Request
                    </span>
                  </button>

                  <button
                    onClick={() => setInquiryType("support")}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      inquiryType === "support"
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <HelpCircle className={`w-6 h-6 mx-auto mb-2 ${
                      inquiryType === "support" ? "text-blue-600" : "text-gray-400"
                    }`} />
                    <span className={`text-sm font-medium ${
                      inquiryType === "support" ? "text-blue-600" : "text-gray-600"
                    }`}>
                      Route Support
                    </span>
                  </button>

                  <button
                    onClick={() => setInquiryType("general")}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      inquiryType === "general"
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <Mail className={`w-6 h-6 mx-auto mb-2 ${
                      inquiryType === "general" ? "text-blue-600" : "text-gray-400"
                    }`} />
                    <span className={`text-sm font-medium ${
                      inquiryType === "general" ? "text-blue-600" : "text-gray-600"
                    }`}>
                      General
                    </span>
                  </button>
                </div>
              </div>

              {/* Help Text */}
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-sm text-blue-800">
                  {inquiryType === "quote" && (
                    <>
                      <strong>Quote Requests:</strong> Questions about routes, pricing, or requesting a new reservation will be sent to{" "}
                      <a href="mailto:hello@getshuttlebee.com" className="underline font-medium">
                        hello@getshuttlebee.com
                      </a>
                    </>
                  )}
                  {inquiryType === "support" && (
                    <>
                      <strong>Route Support:</strong> Questions about an active route or existing reservation will be sent to{" "}
                      <a href="mailto:support@getshuttlebee.com" className="underline font-medium">
                        support@getshuttlebee.com
                      </a>
                    </>
                  )}
                  {inquiryType === "general" && (
                    <>
                      <strong>General Inquiries:</strong> General questions will be sent to{" "}
                      <a href="mailto:hello@getshuttlebee.com" className="underline font-medium">
                        hello@getshuttlebee.com
                      </a>
                    </>
                  )}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg"
                >
                  Send Message
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Contact Information
              </h2>

              <div className="space-y-6">
                <Card className="border-2 hover:border-blue-300 transition-all duration-300 hover-lift">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">Quote Requests</h3>
                        <p className="text-gray-600 mb-3">
                          Questions about routes, pricing, or new reservations
                        </p>
                        <a
                          href="mailto:hello@getshuttlebee.com"
                          className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
                        >
                          hello@getshuttlebee.com
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-blue-300 transition-all duration-300 hover-lift">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center flex-shrink-0">
                        <HelpCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">Route Support</h3>
                        <p className="text-gray-600 mb-3">
                          Help with active routes or existing reservations
                        </p>
                        <a
                          href="mailto:support@getshuttlebee.com"
                          className="text-green-600 hover:text-green-700 font-medium inline-flex items-center"
                        >
                          support@getshuttlebee.com
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-blue-300 transition-all duration-300 hover-lift">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">Phone</h3>
                        <p className="text-gray-600 mb-3">
                          Call us during business hours
                        </p>
                        <a
                          href="tel:+14843242559"
                          className="text-purple-600 hover:text-purple-700 font-medium inline-flex items-center"
                        >
                          (484) 324-2559
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-blue-300 transition-all duration-300 hover-lift">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-600 to-orange-700 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">Service Area</h3>
                        <p className="text-gray-600">
                          Serving Summerside Camps at SCH
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
